import React, { useState, useEffect } from 'react';
import { useHistory, Link, useParams } from 'react-router-dom';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { CircularProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import Modal from '@mui/material/Modal';

import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';

// ── Color tokens ──────────────────────────────────────────────────────────────
const GN = '#0e7490';
const GNL = '#ecfeff';
const GND = '#0c4a6e';

// ── Shared styles ─────────────────────────────────────────────────────────────
const inp2 = { border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', height: '30px', width: '100%', padding: '0 8px', outline: 'none', backgroundColor: '#fff', boxSizing: 'border-box', color: '#1a2e2e' };
const inp2R = { ...inp2, textAlign: 'right' };
const lbl2 = { fontSize: '10px', color: '#6b7280', display: 'block', marginBottom: '2px', fontWeight: 500 };
const ss2 = {
    control: (b) => ({ ...b, minHeight: '30px', height: '30px', fontSize: '12px', borderColor: '#d1d5db', borderRadius: '4px', boxShadow: 'none' }),
    valueContainer: (b) => ({ ...b, height: '30px', padding: '0 8px' }),
    indicatorsContainer: (b) => ({ ...b, height: '30px' }),
    menuPortal: (b) => ({ ...b, zIndex: 3000 }),
    menu: (b) => ({ ...b, fontSize: '12px' }),
};
const ss2Error = {
    control: (b) => ({ ...b, minHeight: '30px', height: '30px', fontSize: '12px', borderColor: '#ef4444', borderWidth: '1.5px', borderRadius: '4px', boxShadow: '0 0 0 2px rgba(239,68,68,0.15)' }),
    valueContainer: (b) => ({ ...b, height: '30px', padding: '0 8px' }),
    indicatorsContainer: (b) => ({ ...b, height: '30px' }),
    menuPortal: (b) => ({ ...b, zIndex: 3000 }),
    menu: (b) => ({ ...b, fontSize: '12px' }),
};
const sectionBox = { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', marginBottom: '8px', overflow: 'hidden' };
const sectionHead = { backgroundColor: GNL, borderBottom: `2px solid ${GN}`, padding: '5px 12px' };
const sectionTitle = { fontSize: '11px', fontWeight: 700, color: GND, textTransform: 'uppercase', letterSpacing: '0.5px' };

const StockTransferEdit = () => {
    const { id } = useParams();
    const history = useHistory();
    const { isAuthenticated, companyId, userId, token, logout } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    }, [isAuthenticated]);

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [toStoreError, setToStoreError] = useState('');

    const [formData, setFormData] = useState({});
    const [storeLocationOptions, setStoreLocationOptions] = useState([]);
    const [selectedFromStore, setSelectedFromStore] = useState(null);
    const [selectedToStore, setSelectedToStore] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectedItemOption, setSelectedItemOption] = useState(null);
    const [itemsOptions, setItemsOptions] = useState([]);
    const [modalData, setModalData] = useState({ ItemId: '', ItemName: '', Quantity: '', BalanceStock: '0' });

    const [itemToDeleteIndex, setItemToDeleteIndex] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    // ── Load store locations ───────────────────────────────────────────────────
    useEffect(() => {
        fetchData('StoreLocationApi', `/${companyId}`, token)
            .then(res => {
                if (res.responseCode === '1111') setStoreLocationOptions(res.response_data);
            })
            .catch(err => console.error('Error fetching store locations:', err));
    }, []);

    // ── Load existing record ───────────────────────────────────────────────────
    useEffect(() => {
        fetchData('StockTransferApi', `/${id}`, token)
            .then(response => {
                if (response.responseCode === '1111') {
                    const header = response.response_data.StockTransfer;
                    const details = response.response_data.StockTransferDetails;
                    setFormData(header);
                    setSelectedItems(details);

                    if (header.FromStoreLocationId) {
                        setSelectedFromStore({ value: header.FromStoreLocationId, label: header.FromStoreLocationName });
                    }
                    if (header.ToStoreLocationId) {
                        setSelectedToStore({ value: header.ToStoreLocationId, label: header.ToStoreLocationName });
                    }
                }
            })
            .catch(err => console.error('Error fetching Stock Transfer:', err));
    }, [id]);

    // ── Item async search ──────────────────────────────────────────────────────
    const fetchItemOptions = async (inputValue = '') => {
        if (!inputValue || inputValue.length <= 1) return [];
        try {
            //const response = await fetchData('StockTransferApi', `/Item/${companyId}/${inputValue}`, token);
            const response = await fetchData('SaleApi', `/Item/${companyId}/${companyId}/${inputValue}`, token);
            if (response.responseCode === '1111') {
                const data = response.response_data || [];
                if (Array.isArray(data)) {
                    setItemsOptions(data);
                    return data.map(item => ({ value: item.Id, label: item.ItemName }));
                }
            }
            return [];
        } catch (error) {
            console.error('Error fetching item options:', error);
            return [];
        }
    };

    const handleItemChange = async (selectedOption) => {
        setSelectedItemOption(selectedOption);
        if (!selectedOption) {
            setModalData({ ItemId: '', ItemName: '', Quantity: '', BalanceStock: '0' });
            return;
        }
        let detail = itemsOptions.find(i => i.Id === selectedOption.value);
        if (!detail) {
            try {
                const res = await fetchData('StockTransferApi', `/Item/${companyId}/${selectedOption.label}`, token);
                if (res.responseCode === '1111' && Array.isArray(res.response_data)) {
                    detail = res.response_data.find(x => x.Id === selectedOption.value);
                }
            } catch (err) {
                console.error('Error fetching fallback item:', err);
            }
        }
        if (!detail) return;
        setModalData({ ItemId: detail.Id, ItemName: detail.ItemName, Quantity: 1, BalanceStock: detail.BalanceStock || '0' });
    };

    const saveItem = () => {
        if (!modalData.ItemId) { setErrorMessage('Item is required.'); return; }
        if (!modalData.Quantity || isNaN(modalData.Quantity) || parseFloat(modalData.Quantity) <= 0) {
            setErrorMessage('Quantity must be a valid positive number.');
            return;
        }
        const duplicate = selectedItems.some(i => i.ItemId === modalData.ItemId);
        if (duplicate) { alert('This item is already added.'); return; }

        const newItem = {
            ItemId: modalData.ItemId,
            ItemName: modalData.ItemName,
            Quantity: parseFloat(modalData.Quantity),
            FromStoreLocationId: selectedFromStore ? selectedFromStore.value : null,
            ToStoreLocationId: selectedToStore ? selectedToStore.value : null,
            IsActive: true,
            CreatedBy: userId,
            CreatedDate: new Date().toISOString(),
        };

        const updated = [newItem, ...selectedItems];
        setSelectedItems(updated);
        setFormData(prev => ({ ...prev, TotalQuantity: updated.reduce((s, i) => s + parseFloat(i.Quantity || 0), 0) }));
        setModalData({ ItemId: '', ItemName: '', Quantity: '', BalanceStock: '0' });
        setSelectedItemOption(null);
        setErrorMessage('');
    };

    const openConfirmDialog = (index) => { setItemToDeleteIndex(index); setConfirmDialogOpen(true); };
    const closeConfirmDialog = () => { setConfirmDialogOpen(false); setItemToDeleteIndex(null); };

    const handleDeleteItem = () => {
        if (itemToDeleteIndex !== null) {
            const updated = selectedItems.filter((_, i) => i !== itemToDeleteIndex);
            setSelectedItems(updated);
            setFormData(prev => ({ ...prev, TotalQuantity: updated.reduce((s, i) => s + parseFloat(i.Quantity || 0), 0) }));
            closeConfirmDialog();
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    // ── Submit ─────────────────────────────────────────────────────────────────
    const handleSubmit = (e) => {
        e.preventDefault();
        setErrorMessage('');

        if (!selectedFromStore) { setErrorMessage('From Store Location is required.'); return; }
        if (!selectedToStore) { setErrorMessage('To Store Location is required.'); return; }
        if (selectedFromStore.value === selectedToStore.value) {
            setErrorMessage('From and To Store Locations cannot be the same.');
            return;
        }
        if (!formData.BillDate) { setErrorMessage('Bill Date is required.'); return; }
        if (selectedItems.length === 0) { setErrorMessage('At least one item must be added.'); return; }

        setIsLoading(true);

        const payload = {
            StockTransfer: {
                ...formData,
                FromStoreLocationId: selectedFromStore.value,
                ToStoreLocationId: selectedToStore.value,
                UpdatedBy: userId,
                UpdatedDate: new Date().toISOString(),
            },
            StockTransferDetails: selectedItems,
        };

        updateData('StockTransferApi', id, payload, token)
            .then(() => {
                history.push('/StockTransferIndex');
            })
            .catch(error => {
                console.error('Error updating Stock Transfer:', error);
                setErrorMessage('Error updating record. Please try again.');
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <div style={{ fontFamily: 'Segoe UI, sans-serif', fontSize: '12px', width: '100%', padding: '6px', boxSizing: 'border-box', backgroundColor: '#f5f5f5' }}>

            {/* ── Top action bar ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', gap: '8px' }}>
                <span style={{ fontWeight: 700, fontSize: '13px', color: GN }}>Stock Transfer Edit</span>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={handleSubmit} disabled={isLoading}
                        style={{ height: '30px', padding: '0 18px', borderRadius: '4px', border: 'none', backgroundColor: GN, color: '#fff', fontWeight: 700, fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {isLoading ? <CircularProgress size={13} color="inherit" /> : null} Update
                    </button>
                    <Link to="/StockTransferIndex"
                        style={{ height: '30px', padding: '0 14px', borderRadius: '4px', border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151', fontWeight: 600, fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                        ← Back
                    </Link>
                </div>
            </div>

            {/* ── Error ── */}
            {errorMessage && (
                <div style={{ marginBottom: '6px', padding: '6px 12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', fontSize: '11px', color: '#ef4444' }}>
                    {errorMessage}
                </div>
            )}

            {/* ══ SECTION 1 — Transfer Information ══ */}
            <div style={sectionBox}>
                <div style={sectionHead}><span style={sectionTitle}>Transfer Information</span></div>
                <div style={{ padding: '8px 12px', display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>

                    {/* Bill No */}
                    <div style={{ flex: '0 0 110px' }}>
                        <label style={lbl2}>Bill No</label>
                        <input type="text" value={`${formData.BillNumber || ''}/${formData.FinYear || ''}`} readOnly style={{ ...inp2, backgroundColor: '#f8fafa' }} />
                    </div>

                    {/* Bill Date */}
                    <div style={{ flex: '0 0 140px' }}>
                        <label style={lbl2}>Bill Date <span style={{ color: '#ef4444' }}>*</span></label>
                        <input type="date" name="BillDate" value={formatDateForInput(formData.BillDate)} onChange={handleChange} style={inp2} />
                    </div>

                    {/* From Store Location */}
                    <div style={{ flex: '1 1 180px' }}>
                        <label style={lbl2}>From Store Location <span style={{ color: '#ef4444' }}>*</span></label>
                        <Select
                            options={storeLocationOptions.map(s => ({ value: s.Id, label: s.Name }))}
                            value={selectedFromStore}
                            onChange={selected => {
                                setSelectedFromStore(selected);
                                setFormData(prev => ({ ...prev, FromStoreLocationId: selected ? selected.value : null }));
                                // If the new From matches the current To, clear To and show error
                                if (selected && selectedToStore && selected.value === selectedToStore.value) {
                                    setToStoreError('To Store cannot be the same as From Store.');
                                    setSelectedToStore(null);
                                    setFormData(prev => ({ ...prev, FromStoreLocationId: selected ? selected.value : null, ToStoreLocationId: null }));
                                }
                            }}
                            isClearable isSearchable placeholder="Select From Store"
                            menuPortalTarget={document.body} styles={ss2}
                        />
                    </div>

                    {/* To Store Location */}
                    <div style={{ flex: '1 1 180px' }}>
                        <label style={lbl2}>To Store Location <span style={{ color: '#ef4444' }}>*</span></label>
                        <Select
                            options={storeLocationOptions.map(s => ({ value: s.Id, label: s.Name }))}
                            value={selectedToStore}
                            onChange={selected => {
                                if (selected && selectedFromStore && selected.value === selectedFromStore.value) {
                                    setToStoreError('To Store cannot be the same as From Store.');
                                    setSelectedToStore(null);
                                    setFormData(prev => ({ ...prev, ToStoreLocationId: null }));
                                } else {
                                    setToStoreError('');
                                    setSelectedToStore(selected);
                                    setFormData(prev => ({ ...prev, ToStoreLocationId: selected ? selected.value : null }));
                                }
                            }}
                            isClearable isSearchable placeholder="Select To Store"
                            menuPortalTarget={document.body}
                            styles={toStoreError ? ss2Error : ss2}
                        />
                        {toStoreError && (
                            <span style={{ fontSize: '10px', color: '#ef4444', marginTop: '2px', display: 'block' }}>
                                {toStoreError}
                            </span>
                        )}
                    </div>

                    {/* Total Qty */}
                    <div style={{ flex: '0 0 100px' }}>
                        <label style={lbl2}>Total Qty</label>
                        <input type="text" value={formData.TotalQuantity || 0} readOnly style={{ ...inp2R, backgroundColor: '#f8fafa', fontWeight: 700, color: GND }} />
                    </div>

                </div>
            </div>

            {/* ══ SECTION 2 — Transfer Items ══ */}
            <div style={sectionBox}>
                <div style={{ ...sectionHead, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px' }}>
                    <span style={sectionTitle}>Transfer Items * ({selectedItems.length} items)</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap' }}>
                        <div style={{ width: '300px' }}>
                            <AsyncSelect
                                cacheOptions loadOptions={fetchItemOptions} value={selectedItemOption} onChange={handleItemChange}
                                placeholder="Search item…" isSearchable menuPortalTarget={document.body}
                                styles={{
                                    control: (b) => ({ ...b, minHeight: '26px', height: '26px', fontSize: '12px', borderColor: GN, borderRadius: '4px', boxShadow: 'none' }),
                                    valueContainer: (b) => ({ ...b, height: '26px', padding: '0 6px' }),
                                    indicatorsContainer: (b) => ({ ...b, height: '26px' }),
                                    menuPortal: (b) => ({ ...b, zIndex: 3000 }),
                                    menu: (b) => ({ ...b, fontSize: '12px' }),
                                }}
                            />
                        </div>
                        <input type="number" value={modalData.Quantity} onChange={e => setModalData(prev => ({ ...prev, Quantity: e.target.value }))}
                            placeholder="Qty" min="1"
                            style={{ ...inp2R, width: '70px', height: '26px' }} />
                        {parseFloat(modalData.BalanceStock) > 0 && (
                            <span style={{ fontSize: '10px', backgroundColor: GNL, color: GND, padding: '2px 6px', borderRadius: '10px', fontWeight: 600, whiteSpace: 'nowrap', border: `1px solid ${GN}` }}>
                                Stock: {modalData.BalanceStock}
                            </span>
                        )}
                        <button onClick={saveItem}
                            style={{ height: '26px', padding: '0 14px', borderRadius: '4px', border: 'none', backgroundColor: GN, color: '#fff', fontWeight: 700, fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            + Add
                        </button>
                    </div>
                </div>

                {/* Items table */}
                <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                    {selectedItems.length === 0 ? (
                        <div style={{ backgroundColor: GNL, padding: '20px', textAlign: 'center', fontSize: '12px', color: GND }}>No items added</div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                            <thead style={{ backgroundColor: GN, color: '#fff', position: 'sticky', top: 0 }}>
                                <tr>
                                    {['Sl No', 'Item', 'Quantity'].map((h, i) => (
                                        <th key={i} style={{ padding: '6px 8px', textAlign: i > 1 ? 'right' : 'left', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {selectedItems.map((item, index) => (
                                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : GNL, borderBottom: '1px solid #e5e7eb' }}
                                        onMouseEnter={e => e.currentTarget.style.backgroundColor = GNL}
                                        onMouseLeave={e => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#fff' : GNL}>
                                        <td style={{ padding: '3px 8px', textAlign: 'center', whiteSpace: 'nowrap', width: '60px' }}>
                                            {index + 1}
                                            <DeleteIcon style={{ color: '#dc2626', cursor: 'pointer', marginLeft: '4px', fontSize: '14px', verticalAlign: 'middle' }} onClick={() => openConfirmDialog(index)} />
                                        </td>
                                        <td style={{ padding: '3px 8px', fontWeight: 600 }}>{item.ItemName}</td>
                                        <td style={{ padding: '2px', width: '100px' }}>
                                            <input type="number" value={item.Quantity}
                                                onChange={e => {
                                                    const updated = [...selectedItems];
                                                    updated[index] = { ...updated[index], Quantity: parseFloat(e.target.value) || 0 };
                                                    setSelectedItems(updated);
                                                    setFormData(prev => ({ ...prev, TotalQuantity: updated.reduce((s, i) => s + parseFloat(i.Quantity || 0), 0) }));
                                                }}
                                                style={{ ...inp2R, height: '24px', borderRadius: '4px' }} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* ── Delete confirm modal ── */}
            <Modal open={confirmDialogOpen} onClose={closeConfirmDialog} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ padding: '20px 24px', backgroundColor: '#fff', borderRadius: '12px', minWidth: '300px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#1a2e2e', marginBottom: '8px' }}>Remove Item?</div>
                    <p style={{ fontSize: '12px', color: '#5a7a7a', marginBottom: '16px' }}>Are you sure you want to remove this item?</p>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button onClick={handleDeleteItem} style={{ padding: '6px 16px', borderRadius: '7px', border: 'none', backgroundColor: '#ef4444', color: '#fff', fontWeight: 700, fontSize: '11px', cursor: 'pointer' }}>Remove</button>
                        <button onClick={closeConfirmDialog} style={{ padding: '6px 16px', borderRadius: '7px', border: '1px solid rgba(0,0,0,0.12)', backgroundColor: '#fff', color: '#5a7a7a', fontWeight: 600, fontSize: '11px', cursor: 'pointer' }}>Cancel</button>
                    </div>
                </div>
            </Modal>

        </div>
    );
};

export default StockTransferEdit;
